import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, UserPlus } from "lucide-react";
import type { HubUserProfile, Opportunity } from "@hub/shared";
import { Avatar, Button, EmptyState, Field, Input, Modal, Pill, SectionTitle, Spinner, Switch, Tabs, Textarea, formatDate, relativeTime, toast } from "@/ui";
import { api, invokeEngine, useSession } from "../_shims";
import { useConnections, useCredits } from "../lib/hooks";
import { CREDITS_VISIBLE } from "../lib/credits";
import { sharedContactInput } from "../lib/contactPayload";
import { PageFrame } from "../components/PageFrame";
import { PeopleYouKnow } from "../components/PeopleYouKnow";
import s from "../network.module.css";

type Tab = "directory" | "connections" | "opportunities" | "shares";

export function ConnectButton({ profile, size = "sm" }: { profile: HubUserProfile; size?: "sm" | "md" }) {
  const qc = useQueryClient();
  const { user, isSignedIn } = useSession();
  const conns = useConnections(isSignedIn && profile.connectionStatus === "pending");
  const incoming = conns.data?.connections.find((c) => c.status === "pending" && c.otherUser.userId === profile.userId && c.toUserId === user?.id);
  const [note, setNote] = useState<string | null>(null);
  const accept = useMutation({
    mutationFn: () => api.respondConnection(incoming!.id, { accept: true }),
    onSuccess: () => {
      toast(`You and ${profile.displayName} are connected.`, "success");
      qc.invalidateQueries({ queryKey: ["network"] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : "Could not accept the request.", "danger"),
  });
  const connect = useMutation({
    mutationFn: (message: string | null) => api.requestConnection({ toUserId: profile.userId, message }),
    onSuccess: () => {
      toast(`Connection request sent to ${profile.displayName}.`, "success");
      qc.invalidateQueries({ queryKey: ["network"] });
      setNote(null);
    },
    onError: (e) => toast(e instanceof Error ? e.message : "Could not send the request.", "danger"),
  });
  if (profile.connectionStatus === "self") return null;
  if (profile.connectionStatus === "accepted") return <Pill tone="success">Connected</Pill>;
  if (profile.connectionStatus === "pending" && incoming) {
    return (
      <div className="u-row">
        <Pill tone="warning">Wants to connect</Pill>
        <Button size={size} variant="primary" onClick={() => accept.mutate()} loading={accept.isPending}>
          Accept
        </Button>
      </div>
    );
  }
  if (profile.connectionStatus === "pending") return <Pill tone="neutral">Request sent</Pill>;
  return (
    <>
      <Button size={size} variant="primary" icon={<UserPlus size={14} />} onClick={() => setNote("")} loading={connect.isPending}>
        Connect
      </Button>
      <Modal
        open={note !== null}
        onClose={() => setNote(null)}
        title={`Connect with ${profile.displayName}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setNote(null)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => connect.mutate(note?.trim() || null)} loading={connect.isPending}>
              Send request
            </Button>
          </>
        }
      >
        <Field label="Add a note" hint="Optional. They see it with your request.">
          <Textarea value={note ?? ""} onChange={(e) => setNote(e.target.value)} rows={3} autoFocus />
        </Field>
      </Modal>
    </>
  );
}

export function ProfileCard({ profile, onOpen }: { profile: HubUserProfile; onOpen?: () => void }) {
  return (
    <div className={s.profileCard} data-testid={`profile-${profile.userId}`}>
      <div className={s.profileHead}>
        <Avatar name={profile.displayName} src={profile.avatarUrl} size={40} seed={profile.userId} />
        <div className="u-grow" style={{ minWidth: 0 }}>
          <div className={s.profileName}>
            {onOpen ? (
              <button type="button" onClick={onOpen} style={{ background: "none", border: 0, padding: 0, font: "inherit", fontWeight: 600, cursor: "pointer", color: "inherit" }}>
                {profile.displayName}
              </button>
            ) : (
              profile.displayName
            )}
          </div>
          {profile.headline && <div className={`${s.profileLine} u-truncate`}>{profile.headline}</div>}
          {profile.location && <div className={s.profileLine}>{profile.location}</div>}
        </div>
        <ConnectButton profile={profile} />
      </div>
      {profile.services.length > 0 && (
        <div className={s.chips}>
          <span className={`${s.small} ${s.muted}`}>Offers</span>
          {profile.services.slice(0, 4).map((sv) => (
            <span key={sv.id} className={s.chip} title={sv.category}>
              {sv.title}
            </span>
          ))}
        </div>
      )}
      {profile.needs.length > 0 && (
        <div className={s.chips}>
          <span className={`${s.small} ${s.muted}`}>Needs</span>
          {profile.needs.slice(0, 4).map((n) => (
            <span key={n.id} className={s.chip} title={n.category}>
              {n.title}
            </span>
          ))}
        </div>
      )}
      {profile.mutualConnections > 0 && <div className={`${s.small} ${s.muted}`}>{profile.mutualConnections} mutual connection{profile.mutualConnections === 1 ? "" : "s"}</div>}
    </div>
  );
}

function DirectoryTab() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [submitted, setSubmitted] = useState("");
  const dir = useQuery({ queryKey: ["network", "directory", submitted], queryFn: () => api.directory({ q: submitted || undefined, limit: 60 }) });
  return (
    <div className="u-col" style={{ gap: 12 }}>
      <form
        className="u-row"
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(q.trim());
        }}
      >
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people on Hub by name, headline, need or offer" aria-label="Search directory" style={{ flex: 1 }} />
        <Button type="submit" icon={<Search size={14} />}>
          Search
        </Button>
      </form>
      {dir.isLoading ? (
        <Spinner />
      ) : dir.isError ? (
        <div className={s.error}>Could not load the directory.</div>
      ) : (dir.data?.users.length ?? 0) === 0 ? (
        <EmptyState title="No one found">Try a different word, or invite people you know to Hub.</EmptyState>
      ) : (
        <div className={s.grid}>
          {dir.data!.users.map((u) => (
            <ProfileCard key={u.userId} profile={u} onOpen={() => navigate(`/network/${u.userId}`)} />
          ))}
        </div>
      )}
    </div>
  );
}

function ConnectionsTab() {
  const { user } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const conns = useConnections();
  const respond = useMutation({
    mutationFn: ({ id, accept }: { id: string; accept: boolean }) => api.respondConnection(id, { accept }),
    onSuccess: (_, v) => {
      toast(v.accept ? "Connection accepted." : "Request declined.", "success");
      qc.invalidateQueries({ queryKey: ["network"] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : "Could not respond.", "danger"),
  });
  const share = useMutation({
    mutationFn: ({ id, sharesNetwork }: { id: string; sharesNetwork: boolean }) => api.updateConnection(id, { sharesNetwork }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["network", "connections"] }),
    onError: (e) => toast(e instanceof Error ? e.message : "Could not update.", "danger"),
  });
  if (conns.isLoading) return <Spinner />;
  const all = conns.data?.connections ?? [];
  const me = user?.id;
  const incoming = all.filter((c) => c.status === "pending" && c.toUserId === me);
  const outgoing = all.filter((c) => c.status === "pending" && c.fromUserId === me);
  const accepted = all.filter((c) => c.status === "accepted");
  // A request someone else made for you (an introduction) names them; the note is theirs, not the other person's.
  const introducer = (c: (typeof all)[number]) => (c.facilitatorUserId && c.facilitatorUserId !== me ? (all.find((x) => x.otherUser.userId === c.facilitatorUserId)?.otherUser.displayName ?? "a connection") : null);
  const Row = ({ c, right }: { c: (typeof all)[number]; right: React.ReactNode }) => (
    <div className={s.connRow}>
      <Avatar name={c.otherUser.displayName} src={c.otherUser.avatarUrl} size={36} seed={c.otherUser.userId} />
      <div style={{ minWidth: 0 }}>
        <div className={s.profileName}>
          <button type="button" onClick={() => navigate(`/network/${c.otherUser.userId}`)} style={{ background: "none", border: 0, padding: 0, font: "inherit", fontWeight: 600, cursor: "pointer", color: "inherit" }}>
            {c.otherUser.displayName}
          </button>
        </div>
        {c.otherUser.headline && <div className={`${s.profileLine} u-truncate`}>{c.otherUser.headline}</div>}
        {introducer(c) && <div className={`${s.small} ${s.muted}`}>Introduced by {introducer(c)}</div>}
        {c.message && <div className={`${s.small} ${s.muted}`}>{introducer(c) ? `${introducer(c)}: ` : ""}"{c.message}"</div>}
      </div>
      <div className="u-row">{right}</div>
    </div>
  );
  return (
    <div className="u-col" style={{ gap: 20 }}>
      <PeopleYouKnow />
      <section>
        <SectionTitle>Requests for you</SectionTitle>
        {incoming.length === 0 ? (
          <div className={`${s.small} ${s.muted}`}>No pending requests.</div>
        ) : (
          <div className={s.stack}>
            {incoming.map((c) => (
              <Row
                key={c.id}
                c={c}
                right={
                  <>
                    <Button size="sm" variant="primary" onClick={() => respond.mutate({ id: c.id, accept: true })} loading={respond.isPending}>
                      Accept
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => respond.mutate({ id: c.id, accept: false })}>
                      Decline
                    </Button>
                  </>
                }
              />
            ))}
          </div>
        )}
      </section>
      <section>
        <SectionTitle>Sent</SectionTitle>
        {outgoing.length === 0 ? (
          <div className={`${s.small} ${s.muted}`}>No requests waiting on others.</div>
        ) : (
          <div className={s.stack}>
            {outgoing.map((c) => (
              <Row key={c.id} c={c} right={<Pill tone="neutral">Sent {relativeTime(c.created)}</Pill>} />
            ))}
          </div>
        )}
      </section>
      <section>
        <SectionTitle action={<span className={`${s.small} ${s.muted}`}>Sharing your network lets matches reach your connections' needs and offers.</span>}>Connected</SectionTitle>
        {accepted.length === 0 ? (
          <div className={`${s.small} ${s.muted}`}>No connections yet. Find people in the directory.</div>
        ) : (
          <div className={s.stack}>
            {accepted.map((c) => {
              const mine = c.fromUserId === me ? c.fromUserSharesNetwork : c.toUserSharesNetwork;
              const theirs = c.fromUserId === me ? c.toUserSharesNetwork : c.fromUserSharesNetwork;
              return (
                <Row
                  key={c.id}
                  c={c}
                  right={
                    <>
                      <span className={`${s.small} ${s.muted}`}>{theirs ? "Shares their network" : "Keeps their network private"}</span>
                      <label className="u-row" style={{ gap: 6 }}>
                        <span className={s.small}>Share my network</span>
                        <Switch on={mine} onChange={(on) => share.mutate({ id: c.id, sharesNetwork: on })} label={`Share my network with ${c.otherUser.displayName}`} />
                      </label>
                    </>
                  }
                />
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

type OpMode = "facilitate" | "request" | "message" | "offer";

/** An offer on a connection's own card needs no introduction: you message them. */
const isDirect = (op: Opportunity) => op.kind === "need_introduction" && op.helperUserId === op.serviceOwnerUserId;

function OpportunitiesTab() {
  const qc = useQueryClient();
  const { isSignedIn } = useSession();
  const ops = useQuery({ queryKey: ["network", "opportunities"], queryFn: () => api.opportunities(), enabled: isSignedIn });
  const [compose, setCompose] = useState<{ op: Opportunity; mode: OpMode } | null>(null);
  const [message, setMessage] = useState("");
  const run = useMutation({
    mutationFn: async ({ op, mode }: { op: Opportunity; mode: OpMode }) => {
      const text = message.trim() || null;
      if (mode === "facilitate") return api.facilitate({ user1Id: op.needOwnerUserId, user2Id: op.serviceOwnerUserId, matchContext: { needId: op.needId, serviceId: op.serviceId, needTitle: op.needTitle, serviceTitle: op.serviceTitle }, message: text });
      if (mode === "message") return api.startConversation({ toUserId: op.serviceOwnerUserId, message: text ?? `I need ${op.needTitle}. You offer ${op.serviceTitle}. Could you help?` });
      if (mode === "request") return api.requestIntroduction({ facilitatorUserId: op.serviceOwnerUserId, targetUserId: op.helperUserId ?? op.serviceOwnerUserId, message: text ?? `Could you introduce me to whoever offers ${op.serviceTitle}? I need ${op.needTitle}.` });
      return api.startConversation({ toUserId: op.needOwnerUserId, message: text ?? `I can help with "${op.needTitle}": I offer ${op.serviceTitle}.` });
    },
    onSuccess: (_, { mode }) => {
      toast(mode === "facilitate" ? "Introduction started. Both sides get a request from you." : mode === "request" ? "Introduction requested." : "Message sent.", "success");
      setCompose(null);
      setMessage("");
      qc.invalidateQueries({ queryKey: ["network"] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : "Could not send.", "danger"),
  });
  if (ops.isLoading) return <Spinner />;
  if (ops.isError) return <div className={s.error}>Could not load opportunities.</div>;
  const d = ops.data!;
  const OpRow = ({ op, action, label }: { op: Opportunity; action: OpMode; label: string }) => (
    <div className={s.itemRow}>
      <div style={{ minWidth: 0 }}>
        <div className={s.itemTitle}>
          <span>{op.kind === "need_introduction" ? "You" : (op.needPersonName ?? "Someone")}</span>
          <span className={s.muted}>{op.kind === "need_introduction" ? "need" : "needs"}</span>
          <span>{op.needTitle}</span>
        </div>
        <div className={s.small}>
          {op.kind === "can_provide" ? "You" : (op.servicePersonName ?? "Someone")} <span className={s.muted}>{op.kind === "can_provide" ? "offer" : "offers"}</span> {op.serviceTitle}
        </div>
        <div className={`${s.small} ${s.muted}`}>
          Match {Math.round(op.score * 100)}%. Expires {formatDate(op.expiresAt)}.
        </div>
      </div>
      <Button size="sm" variant="primary" onClick={() => setCompose({ op, mode: action })}>
        {label}
      </Button>
    </div>
  );
  const empty = d.asFacilitator.length + d.needIntroduction.length + d.canProvide.length === 0;
  return (
    <div className="u-col" style={{ gap: 20 }}>
      {empty && <EmptyState title="No opportunities right now">Opportunities appear when two of your connections could help each other, or when a connection's contact matches one of your needs or offers.</EmptyState>}
      {d.asFacilitator.length > 0 && (
        <section>
          <SectionTitle>You could introduce</SectionTitle>
          <div className={s.sectionHint}>Two people in your network could help each other.{CREDITS_VISIBLE ? " You earn a credit when they connect." : ""}</div>
          <div className={s.stack}>
            {d.asFacilitator.map((op) => (
              <OpRow key={`${op.needId}:${op.serviceId}`} op={op} action="facilitate" label="Introduce" />
            ))}
          </div>
        </section>
      )}
      {d.needIntroduction.length > 0 && (
        <section>
          <SectionTitle>Someone could help you</SectionTitle>
          <div className={s.sectionHint}>A connection, or someone they know, offers what you need.</div>
          <div className={s.stack}>
            {d.needIntroduction.map((op) => (
              <OpRow key={`${op.needId}:${op.serviceId}`} op={op} action={isDirect(op) ? "message" : "request"} label={isDirect(op) ? "Message" : "Ask for intro"} />
            ))}
          </div>
        </section>
      )}
      {d.canProvide.length > 0 && (
        <section>
          <SectionTitle>You could help</SectionTitle>
          <div className={s.sectionHint}>Needs in your connections' networks that match what you offer.</div>
          <div className={s.stack}>
            {d.canProvide.map((op) => (
              <OpRow key={`${op.needId}:${op.serviceId}`} op={op} action="offer" label="Offer help" />
            ))}
          </div>
        </section>
      )}
      <Modal
        open={Boolean(compose)}
        onClose={() => setCompose(null)}
        title={compose?.mode === "facilitate" ? "Introduce two people" : compose?.mode === "request" ? "Ask for an introduction" : compose?.mode === "message" ? `Message ${compose.op.servicePersonName ?? "them"}` : "Offer to help"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCompose(null)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => compose && run.mutate(compose)} loading={run.isPending}>
              Send
            </Button>
          </>
        }
      >
        {compose && (
          <div className="u-col" style={{ gap: 10 }}>
            <div className={s.small}>
              <strong>{compose.op.needTitle}</strong> and <strong>{compose.op.serviceTitle}</strong>
            </div>
            <Field label="Your note" hint="Hub sends only what you write here, nothing else.">
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} autoFocus placeholder={compose.mode === "facilitate" ? "I think you two should talk..." : compose.mode === "request" ? "Could you introduce me to..." : compose.mode === "message" ? "Could you help with..." : "I can help with..."} />
            </Field>
          </div>
        )}
      </Modal>
    </div>
  );
}

function SharesTab() {
  const { user } = useSession();
  const qc = useQueryClient();
  const shares = useQuery({ queryKey: ["network", "shares"], queryFn: () => api.shares(), enabled: Boolean(user) });
  const respond = useMutation({
    mutationFn: ({ id, accept }: { id: string; accept: boolean; sharedBy: { userId: string; name: string } }) => api.respondShare(id, { accept }),
    onSuccess: async (res, v) => {
      if (v.accept && res.contactPayload) {
        try {
          // The copy is marked as shared by the sender: the person page reads "Shared by <name>" where others read "Added from".
          await invokeEngine("createContact", sharedContactInput(res.contactPayload, v.sharedBy));
          toast("Added to your people.", "success");
        } catch {
          toast("Share accepted, but the contact could not be saved locally.", "danger");
        }
      } else {
        toast(v.accept ? "Share accepted." : "Share declined.", "success");
      }
      qc.invalidateQueries({ queryKey: ["network"] });
      qc.invalidateQueries({ queryKey: ["engine"] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : "Could not respond.", "danger"),
  });
  if (shares.isLoading) return <Spinner />;
  if (shares.isError) return <div className={s.error}>Could not load shares.</div>;
  const all = shares.data?.shares ?? [];
  const incoming = all.filter((sh) => sh.toUserId === user?.id);
  const outgoing = all.filter((sh) => sh.fromUserId === user?.id);
  return (
    <div className="u-col" style={{ gap: 20 }}>
      <section>
        <SectionTitle>Shared with you</SectionTitle>
        <div className={s.sectionHint}>{CREDITS_VISIBLE ? "Accepting uses 1 credit and adds the person to your people. The sharer earns 2." : "Accepting adds the person to your people."}</div>
        {incoming.length === 0 ? (
          <div className={`${s.small} ${s.muted}`}>Nothing shared with you yet.</div>
        ) : (
          <div className={s.stack}>
            {incoming.map((sh) => (
              <div key={sh.id} className={s.connRow}>
                <Avatar name={sh.fromUser.displayName} src={sh.fromUser.avatarUrl} size={36} seed={sh.fromUser.userId} />
                <div style={{ minWidth: 0 }}>
                  <div className={s.profileName}>
                    {sh.fromUser.displayName} shared {sh.contactName}
                  </div>
                  {sh.message && <div className={`${s.small} ${s.muted}`}>"{sh.message}"</div>}
                  <div className={`${s.small} ${s.muted}`}>{relativeTime(sh.created)}</div>
                </div>
                <div className="u-row">
                  {sh.status === "pending" ? (
                    <>
                      <Button size="sm" variant="primary" onClick={() => respond.mutate({ id: sh.id, accept: true, sharedBy: { userId: sh.fromUserId, name: sh.fromUser.displayName } })} loading={respond.isPending} title={CREDITS_VISIBLE ? "Uses 1 credit" : undefined}>
                        {CREDITS_VISIBLE ? "Accept (1 credit)" : "Accept"}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => respond.mutate({ id: sh.id, accept: false, sharedBy: { userId: sh.fromUserId, name: sh.fromUser.displayName } })}>
                        Decline
                      </Button>
                    </>
                  ) : (
                    <Pill tone={sh.status === "accepted" ? "success" : "neutral"}>{sh.status}</Pill>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      <section>
        <SectionTitle>You shared</SectionTitle>
        {outgoing.length === 0 ? (
          <div className={`${s.small} ${s.muted}`}>You have not shared anyone yet. Open a person, then choose Share with a Hub user from the more menu.</div>
        ) : (
          <div className={s.stack}>
            {outgoing.map((sh) => (
              <div key={sh.id} className={s.connRow}>
                <Avatar name={sh.toUser.displayName} src={sh.toUser.avatarUrl} size={36} seed={sh.toUser.userId} />
                <div style={{ minWidth: 0 }}>
                  <div className={s.profileName}>
                    {sh.contactName} to {sh.toUser.displayName}
                  </div>
                  <div className={`${s.small} ${s.muted}`}>{relativeTime(sh.created)}</div>
                </div>
                <Pill tone={sh.status === "accepted" ? "success" : sh.status === "declined" ? "danger" : "neutral"}>{sh.status === "accepted" ? (CREDITS_VISIBLE ? "Accepted, +2 credits" : "Accepted") : sh.status}</Pill>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export function CreditsPanel() {
  const credits = useCredits();
  if (!CREDITS_VISIBLE) return null;
  if (credits.isLoading) return <Spinner />;
  if (!credits.data) return null;
  const { status, transactions } = credits.data;
  const unlimited = status.creditsRemaining === null;
  return (
    <div className={s.block} data-testid="credits-panel">
      <div className={s.blockLabel}>Credits</div>
      <div className={s.creditBalance}>{unlimited ? "Unlimited" : status.creditsRemaining}</div>
      <div className={`${s.small} ${s.muted}`}>
        {unlimited ? `${status.planType} plan. Accepting introductions is free; you still earn credits for helping.` : `${status.planType} plan, ${status.creditsPerCycle ?? 0} per cycle. Used ${status.creditsUsed}, earned ${status.creditsEarned}.`}
      </div>
      <div style={{ marginTop: 10 }}>
        {transactions.length === 0 ? (
          <div className={`${s.small} ${s.muted}`}>No activity yet.</div>
        ) : (
          transactions.slice(0, 12).map((t) => (
            <div key={t.id} className={s.txRow}>
              <span className="u-grow">{t.reason.replace(/_/g, " ")}</span>
              <span className={t.amount >= 0 ? s.amountPlus : s.amountMinus}>{t.amount >= 0 ? `+${t.amount}` : t.amount}</span>
              <span className={s.muted}>{relativeTime(t.created)}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export function NetworkPage() {
  const { isSignedIn } = useSession();
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as Tab | null) ?? "directory";
  const tabs = useMemo(
    () => [
      { id: "directory" as Tab, label: "Directory" },
      { id: "connections" as Tab, label: "Connections" },
      { id: "opportunities" as Tab, label: "Opportunities" },
      { id: "shares" as Tab, label: "Shares" },
    ],
    [],
  );
  if (!isSignedIn) {
    return (
      <PageFrame title="Network" subtitle="People on Hub, your connections, and the introductions between them.">
        <EmptyState title="Sign in to see your network">The directory, connections, opportunities and shares live on the Hub server. Your people and notes stay on this device either way.</EmptyState>
      </PageFrame>
    );
  }
  return (
    <PageFrame title="Network" subtitle="People on Hub, your connections, and the introductions between them.">
      <div className={s.split}>
        <div className="u-col" style={{ gap: 14 }}>
          <Tabs tabs={tabs} value={tab} onChange={(t) => setParams({ tab: t })} />
          {tab === "directory" && <DirectoryTab />}
          {tab === "connections" && <ConnectionsTab />}
          {tab === "opportunities" && <OpportunitiesTab />}
          {tab === "shares" && <SharesTab />}
        </div>
        <CreditsPanel />
      </div>
    </PageFrame>
  );
}
